import React from 'react';

import cx from 'classnames';
import cxs from 'cxs';
import memoizeOne from 'memoize-one';
import AutosizeInput from 'react-input-autosize';
import TextareaAutosize from 'react-textarea-autosize';
import _omit from 'lodash.omit';
import _isEqual from 'lodash.isequal';
import { COLOURS } from '../../../common/styles/vars';
import LabelWrapper from '../../LabelWrapper';
import buildInputProps from '../utils/buildInputProps';
import InputContainer from './InputContainer';
import Icon from '../../Icon';
import { formatToASCII } from '../../../../common/utils';

import '../styles/_input.scss';


































const CLEAR_EVENT = { target: { value: '' } };

const ENTER_KEY = 13;

/**
 * Build a unique tooltip ID from an ID prop
 *
 * @param {string} id
 *
 * @return {string}
 */
const getTooltipId = (id) => `${id}-tooltip`;

const DISABLED = {
  color: `${COLOURS.grey.nevada} !important`
};

export default class Input extends React.Component {








  // Getting input props is memoized so we only have to calculate new ones when props
  // change. We don't need to build these props again when only value changes.
  getInputProps = memoizeOne((
  calculatedSuffixIcon,
  valid,
  propsWithoutValue) =>
  {
    const { prefix, type, autoGrow } = propsWithoutValue;
    const hasPrefix = Boolean(prefix);
    const hasSuffix = Boolean(calculatedSuffixIcon);
    const inputProps = buildInputProps({
      valid,
      calculatedSuffixIcon,
      allProps: propsWithoutValue,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      type,
      autoGrow
    });

    return { hasPrefix, hasSuffix, inputProps };
  }, _isEqual);

  constructor(props) {
    super(props);

    if (props.value) {
      /* eslint-disable no-console */
      console.warn(
        'Input manages it\'s own \'value\' in local state for performance. Pass defaultValue if you want to set the starting value. **Ignoring prop**'
      );
    }

    this.state = {
      valid: true,
      value: props.defaultValue || ''
    };

    this.inputRef = React.createRef();
    document.fonts.ready.then(() => this.setState({
      value: props.defaultValue || ''
    }));
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    const { value: prevValue } = prevProps;
    const { value: stateValue } = this.state;

    if (value !== prevValue && value !== stateValue) {
      this.resetValueState(value);
    }
  }

  /**
   * Handle onChange locally for performance reasons. We can update the value
   * in the UI first before calling the parent
   *
   * @param {Object} e (event)
   *
   * @return {void}
   */
  onChange = ({ target: { value } }) => {
    const { onChange, shouldFormateToASCII } = this.props;

    const formattedText = shouldFormateToASCII ? formatToASCII(value) : value;

    this.setState({ value: formattedText }, () => {
      // We can't reuse the event here so have to 'create' one
      if (typeof onChange === 'function') {onChange({ target: { value: formattedText } });}
    });
  };

  onBlur = async () => {
    const { onBlur, autoGrow, shouldFormateToASCII } = this.props;
    const { value } = this.state;

    if (autoGrow && this._autosizeInput) {
      this._autosizeInput.scrollTo({ left: 0 });
    }

    const valid = await this.handleValidate();

    this.setState({ valid });

    const formattedText = shouldFormateToASCII ? formatToASCII(value) : value;

    if (onBlur) {onBlur({ value: formattedText });}
  };

  onFocus = () => {
    const { onFocus } = this.props;

    this.setState({ valid: true });

    if (onFocus) {onFocus();}
  };

  getInputElementFromProps(inputProps) {
    const { multiline, autoGrow, placeholder } = this.props;

    if (autoGrow) {
      return (
        <AutosizeInput
          {...inputProps}
          className={cx(inputProps.className, 'input__editable')}
          inputRef={this.inputRefSetter}
          onChange={this.onChange}
          onPaste={this.onChange}
          placeholder={placeholder}
          onKeyDown={(e) => {
            this.handleEnterPressed(e);
            if (inputProps.onKeyDown) {
              inputProps.onKeyDown(e);
            }
          }} />);


    }

    return multiline ?

    <TextareaAutosize
      {...inputProps}
      minRows={inputProps.rows || 1}
      maxRows={inputProps.maxRows}
      ref={this.texareaRefSetter} /> :


    <input {...inputProps} />;
  }

  resetValueState = (value) => {
    this.setState({ value });
  };

  handleEnterPressed = (event) => {
    const { keyCode } = event;

    if (keyCode !== ENTER_KEY) {return;}

    event.preventDefault();
    event.stopPropagation();

    if (!this._autosizeInput) {return;}

    this._autosizeInput.blur();
  };

  clearValue = () => {
    this.onChange(CLEAR_EVENT);
  };

  buildSuffixIcon = () => {
    const { suffixIcon, suffixIconOnClick, clearButton } = this.props;
    const { valid, value } = this.state;

    if (suffixIconOnClick) {
      return suffixIcon;
    }

    if (!clearButton || !value) {
      return !valid ? 'alert' : suffixIcon;
    }

    return typeof clearButton === 'object' ?
    React.cloneElement(clearButton, { onClick: this.clearValue }) :
    <Icon name="close" size={10} onClick={this.clearValue} color="regent" hoverColor="denim" />;
  };

  /**
   * Sets the input ref
   *
   * @param {Element} el
   * @return {null}
   */
  inputRefSetter = (el) => {
    this._autosizeInput = el;
  };

  texareaRefSetter = (el) => {
    this.textareaRef = el;

    const { inputRefCallback } = this.props;
    if (!inputRefCallback) {
      return;
    }
    inputRefCallback(el);
  };

  handleValidate() {
    const { validate } = this.props;
    const { value } = this.state;

    if (!validate) {return true;}

    return validate(value);
  }

  renderInput({
    calculatedSuffixIcon
  })
  {
    const { disabled, readOnly } = this.props;
    const { value, valid } = this.state;
    const { inputProps } = this.getInputProps(
      calculatedSuffixIcon,
      valid,
      _omit(this.props, 'value')
    );

    return this.getInputElementFromProps({
      ...inputProps,
      value,
      className: `${cx(inputProps.className, 'input__element')} ${cxs((disabled || readOnly) && DISABLED)}`
    });
  }

  render() {
    const {
      label,
      id,
      prefix,
      errorMessage,
      className,
      disabled,
      readOnly,
      labelProps,
      prefixCanTakeFocus,
      multiline,
      autoGrow,
      type,
      suffixIconOnClick,
      hideArrows
    } = this.props;
    const { valid } = this.state;
    const calculatedSuffixIcon = this.buildSuffixIcon();
    const customInputClass = className ? { [className]: !label } : void 0;
    const iconId = getTooltipId(id);

    return (
      <LabelWrapper
        {...labelProps}
        id={iconId}
        className={cx('input-label', className)}
        label={label}
        disabled={disabled || readOnly}>

        <InputContainer
          className={cx(customInputClass)}
          prefix={prefix}
          suffixIcon={calculatedSuffixIcon}
          suffixIconOnClick={suffixIconOnClick}
          valid={valid}
          errorMessage={errorMessage}
          id={iconId}
          prefixCanTakeFocus={prefixCanTakeFocus}
          multiline={multiline && !autoGrow}
          disabled={disabled}
          readOnly={readOnly}
          autoGrow={autoGrow}
          type={type}
          hideArrows={hideArrows}>

          {this.renderInput({ calculatedSuffixIcon })}
        </InputContainer>
      </LabelWrapper>);

  }
}

Input.defaultProps = {
  className: void 0,
  disabled: false,
  readOnly: false,
  label: void 0,
  prefix: void 0,
  suffixIcon: void 0,
  errorMessage: void 0,
  clearButton: void 0
};